import { ErrorSection, Fieldset, Input } from "../Components";
import { FormEvent, useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { addStripeOfficePaymentMethod } from "api/services";

import {
  B4,
  Box,
  Button,
  ButtonGroup,
  P,
  Stack,
  Text
} from "@adfenix/alexandria";
import { useTranslation } from "react-i18next";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import Sentry from "lib/sentry/init";
import { StripeCardElement } from "@stripe/stripe-js";

const options = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      letterSpacing: "0.025em",
      fontFamily: "Source Code Pro, monospace",
      padding: "22px",

      "::placeholder": {
        color: "#aab7c4"
      }
    },
    invalid: {
      color: "#9e2146"
    }
  }
};

interface StripeFormProps {
  clientSecret: string;
  officeId: string;
  onSuccess: () => void;
  handleCancel: () => void;
  isCardListEmpty?: boolean;
  hideDefaultCheckbox?: boolean;
}

export const OfficeSaveCardStripeForm = ({
  clientSecret,
  officeId,
  onSuccess,
  handleCancel,
  isCardListEmpty,
  hideDefaultCheckbox
}: StripeFormProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loadingStripe, setLoadingStripe] = useState(false);
  const [isSetAsDefault, setIsSetAsDefault] = useState(
    isCardListEmpty || false
  );
  const [stripeError, setStripeError] = useState<string | undefined>();
  const { t } = useTranslation();

  const {
    mutateAsync,
    isLoading: isAddingOfficePaymentMethod,
    error: errorAddingOfficePaymentMethod
  } = useMutation(addStripeOfficePaymentMethod);

  const [card, setCard] = useState<StripeCardElement | null>(null);

  useEffect(() => {
    if (!elements) return;
    const cardElement = elements.getElement("card");
    setCard(cardElement);
  }, [elements]);

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setStripeError(undefined);

    if (stripe && card) {
      setLoadingStripe(true);

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card
        }
      });

      setLoadingStripe(false);

      if (result.error) {
        setStripeError(result.error.message);
      } else {
        const paymentMethod = result.setupIntent?.payment_method;

        try {
          if (paymentMethod) {
            await mutateAsync({
              paymentMethodId:
                typeof paymentMethod === "string"
                  ? paymentMethod
                  : paymentMethod.id,
              subjectId: officeId,
              setAsActive: isSetAsDefault
            });
            onSuccess();
          }
        } catch {
          Sentry.captureException(
            `${result.error},
            Error adding office credit card`
          );
          setStripeError(t("billing_could_not_add_card_error_message"));
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="xs">
        <section>
          <Box>
            <B4>{t("billing_stripe_add_credit_card_title")}</B4>

            <Box pY="xs2">
              <P>{t("billing_stripe_add_credit_card_number_title")}</P>
            </Box>

            <Fieldset
              style={{
                padding: "0 8px",
                borderColor: "#cfd8e2"
              }}
            >
              <label>
                <Input as="div">
                  <CardElement options={options} />
                </Input>
              </label>
            </Fieldset>
          </Box>
        </section>

        {Boolean(stripeError || errorAddingOfficePaymentMethod) && (
          <ErrorSection>
            <h4>{t("billing_stripe_generic_error_message")}</h4>
            {stripeError && <h4>{stripeError}</h4>}
          </ErrorSection>
        )}

        {!isCardListEmpty && !hideDefaultCheckbox ? (
          <>
            <input
              onChange={ev => {
                setIsSetAsDefault(Boolean(ev.currentTarget.checked));
              }}
              id="isDefault"
              type="checkbox"
              style={{ appearance: "none" }}
              className="is-checkradio"
            />

            <Text as="label" htmlFor="isDefault">
              {t("billing_stripe_use_card_as_default_checkbox")}
            </Text>
          </>
        ) : null}
      </Stack>
      <ButtonGroup display="flex" justifyContent="end">
        {
          <Button
            logLabel="cancel-save-card-button"
            variant="secondary"
            onClick={handleCancel}
          >
            {t("billing_credit_card_cancel_button_title")}
          </Button>
        }

        <Button
          logLabel="save-card-button"
          variant="primary"
          type="submit"
          isLoading={loadingStripe || isAddingOfficePaymentMethod}
        >
          {t("billing_credit_card_save_button_title")}
        </Button>
      </ButtonGroup>
    </form>
  );
};
